import type { PureQueryOptions } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client';
import {
  faExternalLink,
  faEye,
  faEyeSlash,
  faFont,
  faPenToSquare,
  faPlus,
  faTextSlash,
  faVideoCamera,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type {
  Entity,
  PrimitiveCollection,
  ScreenSpaceEventHandler,
  Viewer,
} from 'cesium';
import {
  Cartesian2,
  Cartesian3,
  Cartographic,
  ConstantPositionProperty,
  ConstantProperty,
  HeadingPitchRoll,
  JulianDate,
} from 'cesium';
import * as R from 'ramda';
import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { JoystickShape } from 'react-joystick-component';
import type { LinkProps } from 'react-router';
import { Link, useParams } from 'react-router';
import invariant from 'tiny-invariant';
import { ApolloProviderV4 } from '~/apollo/client-v4';
import { graphql } from '~/apollo/generated/v4';
import type {
  CesiumLocationPartsFragment,
  CreateSupportingObjectPlacementMutationVariables,
  CrossSection,
  FaciesSchema,
  FieldPicture,
  FilePartsFragment,
  GigaPan,
  MiniModel,
  OutcropSoPlacementsPageQuery,
  OutcropSoPlacementsPageQueryVariables,
  Photo360,
  Picture as SchemaPicture,
  PlacementGeorefPartsFragment,
  PlacementPicturePartsFragment,
  PlacementSoPlacementPartsFragment,
  Production,
  ReservoirModel,
  SedimentaryLog,
  TrainingImage,
  UpdateSupportingObjectDefaultCameraMutationVariables,
  UpdateSupportingObjectPlacementLocationMutationVariables,
  UpdateSupportingObjectPlacementMutationVariables,
  Variogram,
  Video,
  WellLog,
} from '~/apollo/generated/v4/graphql';
import {
  CesiumPlacementType,
  LifecycleStatus,
} from '~/apollo/generated/v4/graphql';
import {
  addIconPoint,
  addInfoBoxIconPoint,
  addOutcropCenterPoint,
  addOutcropOutline,
  addWallImage,
  convertCartesian3Tollhhpr,
  convertFromCartesian,
  enableEntityClickPlacement,
  enableEntityInfoBox,
  getCameraCartographic,
  getCurrentWallPosition,
  getCurrentZoomDistance,
  rotateCartesian3,
  // setBillboardSize,
  setWallSize,
  TerrainProvider,
  toggleEntityLabelVisibility,
  toggleEntityVisibility,
  updateCurrentWallPosition,
  updateCurrentWallheight,
  updateEntityLabel,
  type TilesetParams,
} from '~/components/cesium/cesiumUtils';
import {
  CesiumViewer,
  type LatLngHeightHPR,
} from '~/components/cesium/CesiumViewer';
import SignalJoystick from '~/components/cesium/Joystick';
import type { IJoystickUpdateEvent } from '~/components/cesium/vom-placement-fields';
import type { iconFileNames } from '~/components/common/CesiumIconSelector';
import { IconSelector } from '~/components/common/CesiumIconSelector';
import { Heading } from '~/components/common/Heading';
import { ExpandedIcon } from '~/components/common/icons/ExpandedIcon';
import { NotFound } from '~/components/common/NotFound';
import { SpinnerIcon } from '~/components/common/SpinnerIcon';
import { useBreadcrumb } from '~/components/layout/Breadcrumb';
import { useFullWidthContext } from '~/components/layout/fullWidthContext';
import { gigaPanUrl } from '~/components/supporting-object/giga-pan-viewer';
import { Button } from '~/components/ui/button';
import { SOPlacementStatusModal } from '~/components/upload/supporting-object/so-placement-status-modal';
import { studyRoute, uploadOutcropUpdateRoute } from '~/paths';
import { cn, rejectNil } from '~/utils/common';

const outcropSOPlacementsPageQuery = graphql(`
  query OutcropSOPlacementsPage($id: ID!) {
    outcropGet(id: $id) {
      id
      name
      center {
        lat
        lng
      }
      outline {
        ...placementGeorefParts
      }
      virtualOutcropModels {
        id
        name
        cesiumAsset {
          ...cesiumAssetParts
          tilesetToken {
            token
          }
          location {
            ...cesiumLocationParts
          }
          defaultCamera {
            ...cesiumLocationParts
          }
        }
      }
      pictures {
        ...placementPictureParts
        placement {
          ...placementSOPlacementParts
        }
      }
      facies {
        id
        name
        pictures {
          ...placementPictureParts
        }
        placement {
          ...placementSOPlacementParts
        }
      }
      crossSections {
        id
        name
        georeferences {
          ...placementGeorefParts
        }
        pictures {
          ...placementPictureParts
        }
        placement {
          ...placementSOPlacementParts
        }
      }
      sedimentaryLogs {
        id
        name
        georeferences {
          ...placementGeorefParts
        }
        pictures {
          ...placementPictureParts
        }
        placement {
          ...placementSOPlacementParts
        }
      }
      wellLogs {
        id
        name
        pictures {
          ...placementPictureParts
        }
        placement {
          ...placementSOPlacementParts
        }
      }
      production {
        id
        name
        pictures {
          ...placementPictureParts
        }
        placement {
          ...placementSOPlacementParts
        }
      }
      reservoirModels {
        id
        name
        pictures {
          ...placementPictureParts
        }
        placement {
          ...placementSOPlacementParts
        }
      }
      trainingImages {
        id
        name
        pictures {
          ...placementPictureParts
        }
        placement {
          ...placementSOPlacementParts
        }
      }
      variograms {
        id
        name
        pictures {
          ...placementPictureParts
        }
        placement {
          ...placementSOPlacementParts
        }
      }
      gigaPans {
        id
        name
        gigaPanHash
        georeferences {
          ...placementGeorefParts
        }
        pictures {
          ...placementPictureParts
        }
        placement {
          ...placementSOPlacementParts
        }
      }
      fieldPictures {
        id
        description
        file {
          ...fileParts
        }
        placement {
          ...placementSOPlacementParts
        }
      }
      miniModels {
        id
        name
        url
        placement {
          ...placementSOPlacementParts
        }
      }
      photo360s {
        id
        name
        url
        placement {
          ...placementSOPlacementParts
        }
      }
      videos {
        id
        name
        url
        placement {
          ...placementSOPlacementParts
        }
      }

      studies {
        id
        name
        collectedBy
        collectedYear
        pictures {
          outcropTagId
          ...placementPictureParts
          placement {
            ...placementSOPlacementParts
          }
        }
        facies {
          id
          name
          outcropTagId
          pictures {
            ...placementPictureParts
          }
          placement {
            ...placementSOPlacementParts
          }
        }
        crossSections {
          id
          name
          outcropTagId
          georeferences {
            ...placementGeorefParts
          }
          pictures {
            ...placementPictureParts
          }
          placement {
            ...placementSOPlacementParts
          }
        }
        sedimentaryLogs {
          id
          name
          outcropTagId
          georeferences {
            ...placementGeorefParts
          }
          pictures {
            ...placementPictureParts
          }
          placement {
            ...placementSOPlacementParts
          }
        }
        wellLogs {
          id
          name
          outcropTagId
          pictures {
            ...placementPictureParts
          }
          placement {
            ...placementSOPlacementParts
          }
        }
        production {
          id
          name
          outcropTagId
          pictures {
            ...placementPictureParts
          }
          placement {
            ...placementSOPlacementParts
          }
        }
        reservoirModels {
          id
          name
          outcropTagId
          pictures {
            ...placementPictureParts
          }
          placement {
            ...placementSOPlacementParts
          }
        }
        trainingImages {
          id
          name
          outcropTagId
          pictures {
            ...placementPictureParts
          }
          placement {
            ...placementSOPlacementParts
          }
        }
        variograms {
          id
          name
          outcropTagId
          pictures {
            ...placementPictureParts
          }
          placement {
            ...placementSOPlacementParts
          }
        }
        gigaPans {
          id
          name
          outcropTagId
          gigaPanHash
          georeferences {
            ...placementGeorefParts
          }
          pictures {
            ...placementPictureParts
          }
          placement {
            ...placementSOPlacementParts
          }
        }
      }
    }
  }
`);

const createSOPlacementMutation = graphql(`
  mutation CreateSupportingObjectPlacement(
    $input: SupportingObjectPlacementCreateInput!
  ) {
    supportingObjectPlacementCreate(input: $input) {
      result {
        ...placementSOPlacementParts
      }
    }
  }
`);

const updateSOPlacementMutation = graphql(`
  mutation UpdateSupportingObjectPlacement(
    $id: ID!
    $input: SupportingObjectPlacementUpdateInput!
  ) {
    supportingObjectPlacementUpdate(id: $id, input: $input) {
      result {
        ...placementSOPlacementParts
      }
    }
  }
`);

const updateSOPlacementLocationMutation = graphql(`
  mutation UpdateSupportingObjectPlacementLocation(
    $id: ID!
    $input: SupportingObjectPlacementSaveLocationInput!
  ) {
    supportingObjectPlacementSaveLocation(id: $id, input: $input) {
      result {
        ...placementSOPlacementParts
      }
    }
  }
`);

const updateSOPlacementDefaultCameraMutation = graphql(`
  mutation UpdateSupportingObjectDefaultCamera(
    $id: ID!
    $input: SupportingObjectPlacementSaveDefaultCameraInput!
  ) {
    supportingObjectPlacementSaveDefaultCamera(id: $id, input: $input) {
      result {
        ...placementSOPlacementParts
      }
    }
  }
`);

type Outcrop = NonNullable<OutcropSoPlacementsPageQuery['outcropGet']>;
type Vom = Outcrop['virtualOutcropModels'][number];
type Study = Outcrop['studies'][number];
type Picture = PlacementPicturePartsFragment;
type Georeference = PlacementGeorefPartsFragment;
type Placement = PlacementSoPlacementPartsFragment;
type PlacementOutcrop = Outcrop['pictures'][number]['placement'];
type File = FilePartsFragment;

// TODO: This should be a discriminated union
export type SO = {
  __typename?:
    | SchemaPicture['__typename']
    | FaciesSchema['__typename']
    | CrossSection['__typename']
    | SedimentaryLog['__typename']
    | WellLog['__typename']
    | Production['__typename']
    | ReservoirModel['__typename']
    | TrainingImage['__typename']
    | Variogram['__typename']
    | GigaPan['__typename']
    | FieldPicture['__typename']
    | MiniModel['__typename']
    | Photo360['__typename']
    | Video['__typename'];
  id: string;
  name: string;
  file?: File;
  pictures?: Picture[];
  outcropTagId?: number | null | undefined;
  georeference?: Georeference;
  placement?: Placement | PlacementOutcrop;
  gigaPanHash?: string | null | undefined;
  url?: string;
  pictureUrl?: string;
  thumbnailUrl?: string;
};

type IconFileNames = (typeof iconFileNames)[number];

export const iconDefaults: Record<string, IconFileNames> = {
  Picture: 'Photo',
  FaciesSchema: 'Depositional Setting',
  CrossSection: 'XSection',
  SedimentaryLog: 'SediLog',
  WellLog: 'WellLog',
  Production: 'Doc',
  ReservoirModel: 'Depositional Setting',
  TrainingImage: 'Photo',
  Variogram: 'Map',
  GigaPan: 'Weblink',
  FieldPicture: 'Photo',
  MiniModel: 'MiniModel',
  Photo360: '360',
  Video: 'Video',
  Marker: 'Marker',
};

type PlacementPageContextValue = {
  outcrop: Outcrop;
  viewer: Viewer | null;
  setViewer: (viewer: Viewer) => void;
  visibleTilesets: TilesetParams[];
  toggleTilesetVisibility: (assetToken: string) => void;
  toggleSOVisibility: (soId: string) => boolean;
  placementMode: boolean;
  showPlacementMode: (value: boolean, item: SO | null) => void;
  currentPlacement: LatLngHeightHPR | null;
  setCurrentPlacement: (placement: LatLngHeightHPR) => void;
  selectedSO: SO | null;
  setSelectedSO: (so: SO | null) => void;
  eventHandler: ScreenSpaceEventHandler | null;
  setEventHandler: (eh: ScreenSpaceEventHandler | null) => void;
  currentEntity: Entity | null;
  setCurrentEntity: (entity: Entity | null) => void;
  currentEntityType: CesiumPlacementType;
  setCurrentEntityType: (type: CesiumPlacementType) => void;
  flyToLocation: (dc: CesiumLocationPartsFragment) => void;
  imageSize: { width: number; height: number };
  setImageSize: ({ width, height }: { width: number; height: number }) => void;
  imageSrc: string | undefined;
  setImageSrc: (src: string | undefined) => void;
  cameraZoomLevel: number;
  setCameraZoomLevel: (l: number) => void;
  currentIcon: string | undefined;
  setCurrentIcon: (icon: string | undefined) => void;
  hideTilesets: TilesetParams[];
  setHideTilesets: (tsParams: TilesetParams[]) => void;
  currentDisplayName: string;
  setCurrentDisplayName: (name: string) => void;
  primitiveCollection: PrimitiveCollection | null;
  setPrimitiveCollection: (pc: PrimitiveCollection | null) => void;
  centerPoint: Entity | null;
  setCenterPoint: (ep: Entity | null) => void;
  currentPanelRotation: number;
  setCurrentPanelRotation: (degrees: number) => void;
};

const PlacementPageContext = createContext<PlacementPageContextValue | null>(
  null,
);

export function isIframeType(so: SO): boolean {
  if (
    so.__typename &&
    ['MiniModel', 'Photo360', 'Video', 'GigaPan'].includes(so.__typename)
  ) {
    return true;
  }
  return false;
}

export function initialTilesets(outcrop: Outcrop): TilesetParams[] {
  return outcrop.virtualOutcropModels
    .map(vom => vom.cesiumAsset)
    .reduce<TilesetParams[]>((acc, cur) => {
      if (
        cur &&
        cur.tilesetToken?.token &&
        cur.tilesetUrlLocal &&
        cur.location
      ) {
        acc.push({
          transform: {
            location: Cartographic.fromDegrees(
              cur.location.longitude,
              cur.location.latitude,
              cur.location.height,
            ),
            orientation: new HeadingPitchRoll(
              cur.location.heading,
              cur.location.pitch,
              cur.location.roll,
            ),
          },
          assetToken: cur.tilesetToken.token,
          localPath: cur.tilesetUrlLocal,
        });
      }
      return acc;
    }, []);
}

function PlacementPageContextProvider({
  outcrop,
  children,
}: {
  outcrop: Outcrop;
  children: ReactNode;
}) {
  const availableTilesets = useMemo(() => initialTilesets(outcrop), [outcrop]);

  const [viewer, setViewer] = useState<Viewer | null>(null);
  const [visibleTilesets, setVisibleTilesets] = useState<TilesetParams[]>([
    ...availableTilesets,
  ]);
  const [hideTilesets, setHideTilesets] = useState<TilesetParams[]>([]);
  const [placementMode, setPlacementMode] = useState<boolean>(false);
  const [selectedSO, setSelectedSO] = useState<SO | null>(null);
  const [eventHandler, setEventHandler] =
    useState<ScreenSpaceEventHandler | null>(null);
  const [currentPlacement, setCurrentPlacement] =
    useState<LatLngHeightHPR | null>(null);
  const [currentEntity, setCurrentEntity] = useState<Entity | null>(null);
  const [currentEntityType, setCurrentEntityType] =
    useState<CesiumPlacementType>(CesiumPlacementType.Point);
  const [imageSize, setImageSizeState] = useState<{
    width: number;
    height: number;
  }>({ width: 0, height: 0 });
  const [imageSrc, setImageSrcState] = useState<string>();
  const [cameraZoomLevel, setCameraZoomLevel] = useState<number>(0.0001);
  const [currentIcon, setCurrentIcon] = useState<string>();
  const [currentDisplayName, setCurrentDisplayName] = useState<string>('');
  const [primitiveCollection, setPrimitiveCollection] =
    useState<PrimitiveCollection | null>(null);
  const [centerPoint, setCenterPoint] = useState<Entity | null>(null);
  const [currentPanelRotation, setCurrentPanelRotation] = useState<number>(0);

  async function setImageSrc(src: string | undefined) {
    if (!src) {
      return undefined;
    }
    const img = new Image();
    if (src) {
      img.src = src;
      await img.decode();
      setImageSize({ width: img.width, height: img.height });
      setImageSrcState(src);
    }
  }

  function setImageSize({ width, height }: { width: number; height: number }) {
    if (currentEntity) {
      if (
        currentEntityType === CesiumPlacementType.Panel
        // currentEntity.billboard
      ) {
        // currentEntity.billboard.height = new ConstantProperty(height);
        // currentEntity.billboard.width = new ConstantProperty(width);
        setWallSize(viewer, currentEntity, width, height);
      }
    }
    setImageSizeState({ width, height });
  }

  function flyToLocation(loc: CesiumLocationPartsFragment) {
    viewer?.scene.camera.flyTo({
      destination: Cartesian3.fromDegrees(
        loc.longitude,
        loc.latitude,
        loc.height,
      ),
      orientation: new HeadingPitchRoll(loc.heading, loc.pitch, loc.roll),
    });
  }

  function toggleTilesetVisibility(assetToken: string) {
    const isVisible = !!visibleTilesets.find(
      ts => ts.assetToken === assetToken,
    );

    setVisibleTilesets(prev => {
      if (isVisible) {
        return prev.filter(ts => ts.assetToken !== assetToken);
      }

      const tileset = availableTilesets.find(
        ts => ts.assetToken === assetToken,
      );
      if (tileset) {
        return [...prev, tileset];
      } else {
        return prev;
      }
    });
  }

  const addEntity =
    (viewer: Viewer) =>
    async (item: SO): Promise<Entity | null> => {
      if (item.placement?.location) {
        const position = {
          latitude: item.placement.location.latitude,
          longitude: item.placement.location.longitude,
          height: item.placement.location.height,
          heading: item.placement.location.heading,
          pitch: item.placement.location.pitch,
          roll: item.placement.location.roll,
        } satisfies LatLngHeightHPR;
        let picUrl = item.thumbnailUrl;
        if (!picUrl && item.pictures) {
          picUrl = item.pictures.at(0)?.thumbnailUrl;
        }
        const itemId = `${item.__typename}-${item.id}`;
        if (viewer.entities.getById(itemId) === undefined && item.placement) {
          if (
            item.placement.type === CesiumPlacementType.Panel &&
            picUrl &&
            item.placement.panelWidth &&
            item.placement.panelHeight
          ) {
            return addWallImage(
              viewer,
              itemId,
              picUrl,
              position,
              item.placement?.displayName || item.name,
              item.placement.panelWidth,
              item.placement.panelHeight,
            );
          } else if (
            item.__typename &&
            isIframeType(item) &&
            (item.url || item.gigaPanHash)
          ) {
            const iframeUrl = item.url || gigaPanUrl(item.gigaPanHash || '', 0);
            return await addInfoBoxIconPoint({
              viewer,
              id: itemId,
              panelUrl: iframeUrl,
              position,
              label: item.placement?.displayName || item.name,
              iframe: true,
              iconName: item.placement.icon || iconDefaults[item.__typename],
              width: item.placement.panelWidth ?? undefined,
              height: item.placement.panelHeight ?? undefined,
            });
          } else if (picUrl && item.__typename) {
            return await addInfoBoxIconPoint({
              viewer,
              id: itemId,
              panelUrl: picUrl,
              position,
              label: item.placement?.displayName || item.name,
              iframe: false,
              iconName: item.placement.icon || iconDefaults[item.__typename],
              width: item.placement.panelWidth ?? undefined,
              height: item.placement.panelHeight ?? undefined,
            });
          } else {
            return addIconPoint({
              viewer,
              id: itemId,
              icon:
                item.placement.icon ||
                iconDefaults[item.__typename || 'Marker'],
              position,
              label: item.placement?.displayName || item.name,
            });
          }
        }
      }
      return null;
    };

  const createAllEntities = () => {
    if (viewer) {
      // viewer.entities.removeAll();
    }
    if (viewer) {
      const addToViewer = addEntity(viewer);
      outcrop.crossSections.forEach(addToViewer);
      outcrop.pictures.forEach(addToViewer);
      outcrop.facies.forEach(addToViewer);
      outcrop.sedimentaryLogs.forEach(addToViewer);
      outcrop.wellLogs.forEach(addToViewer);
      outcrop.production.forEach(addToViewer);
      outcrop.reservoirModels.forEach(addToViewer);
      outcrop.trainingImages.forEach(addToViewer);
      outcrop.variograms.forEach(addToViewer);
      outcrop.gigaPans.forEach(addToViewer);
      outcrop.miniModels.forEach(addToViewer);
      outcrop.photo360s.forEach(addToViewer);
      outcrop.videos.forEach(addToViewer);
      outcrop.fieldPictures.forEach(fp => {
        const newFp = {
          ...fp,
          id: fp.id,
          name: fp.description ?? `Field Picture ${fp.id}`,
        };
        addToViewer(newFp);
      });

      outcrop.studies.forEach(study => {
        study.crossSections.forEach(addToViewer);
        study.pictures.forEach(addToViewer);
        study.facies.forEach(addToViewer);
        study.sedimentaryLogs.forEach(addToViewer);
        study.wellLogs.forEach(addToViewer);
        study.production.forEach(addToViewer);
        study.reservoirModels.forEach(addToViewer);
        study.trainingImages.forEach(addToViewer);
        study.variograms.forEach(addToViewer);
        study.gigaPans.forEach(addToViewer);
        outcrop.fieldPictures.forEach(fp => {
          const newFp = {
            ...fp,
            id: fp.id,
            name: fp.description ?? `Field Picture ${fp.id}`,
          };
          addToViewer(newFp);
        });
      });
    }
  };
  createAllEntities();

  const addCenterPoint = useCallback(
    (show: boolean) => {
      if (viewer && outcrop.center) {
        const center = outcrop.center;
        const centerPoint = addOutcropCenterPoint(
          viewer,
          {
            latitude: center.lat,
            longitude: center.lng,
          },
          show,
        );
        setCenterPoint(centerPoint);
      }
    },
    [viewer, outcrop.center],
  );

  const addOutline = useCallback(
    (show: boolean) => {
      if (viewer && outcrop.outline) {
        const outline = outcrop.outline;
        const positions = outline.data
          ? outline.data.map(d => {
              return { longitude: d.lng, latitude: d.lat };
            })
          : [];
        const primColl = addOutcropOutline(viewer, positions, show);
        setPrimitiveCollection(primColl);
      }
    },
    [viewer, outcrop.outline],
  );

  useEffect(() => {
    addCenterPoint(false);
    addOutline(false);
  }, [addCenterPoint, addOutline]);

  function toggleSOVisibility(soId: string) {
    if (!viewer) return false;
    const result = toggleEntityVisibility(viewer, soId);
    return result?.isShowing ?? false;
  }

  function showPlacementMode(value: boolean, item: SO | null) {
    setPlacementMode(value);
    setSelectedSO(item);
    const defaultIcon =
      item && item.__typename ? iconDefaults[item.__typename] : 'Marker';
    setCurrentIcon(defaultIcon);
    if (eventHandler) {
      eventHandler.destroy();
      setEventHandler(null);
    }
  }

  return (
    <PlacementPageContext.Provider
      value={{
        outcrop,
        toggleTilesetVisibility,
        setViewer,
        viewer,
        visibleTilesets,
        toggleSOVisibility,
        showPlacementMode,
        selectedSO,
        setSelectedSO,
        placementMode,
        setCurrentPlacement,
        currentPlacement,
        setEventHandler,
        eventHandler,
        setCurrentEntity,
        currentEntity,
        currentEntityType,
        setCurrentEntityType,
        flyToLocation,
        imageSize,
        setImageSize,
        imageSrc,
        setImageSrc,
        cameraZoomLevel,
        setCameraZoomLevel,
        currentIcon,
        setCurrentIcon,
        hideTilesets,
        setHideTilesets,
        currentDisplayName,
        setCurrentDisplayName,
        primitiveCollection,
        setPrimitiveCollection,
        centerPoint,
        setCenterPoint,
        currentPanelRotation,
        setCurrentPanelRotation,
      }}
    >
      {children}
    </PlacementPageContext.Provider>
  );
}

function usePlacementPageContext() {
  const ctx = useContext(PlacementPageContext);
  invariant(ctx, 'PlacementPageContext not initialized!');
  return ctx;
}

function UploadOutcropSOPlacementsPage() {
  const params = useParams();
  invariant(params.outcropId);
  const outcropId = parseInt(params.outcropId);

  useBreadcrumb(
    'routes/upload/model/outcrop/$outcropId/so-placements',
    'SO Placements',
  );

  const { data, loading } = useQuery(outcropSOPlacementsPageQuery, {
    variables: { id: outcropId },
  });

  const outcrop = data?.outcropGet;

  if (loading) return <SpinnerIcon />;
  if (!outcrop) return <NotFound />;

  return (
    <PlacementPageContextProvider outcrop={outcrop}>
      <PageInner outcrop={outcrop} />
    </PlacementPageContextProvider>
  );
}

function PageInner({ outcrop }: { outcrop: Outcrop }) {
  const {
    setViewer,
    viewer,
    visibleTilesets,
    showPlacementMode,
    placementMode,
    selectedSO,
    setSelectedSO,
    setCurrentPlacement,
    currentPlacement,
    setCurrentEntity,
    currentEntity,
    setCurrentEntityType,
    currentEntityType,
    eventHandler,
    setEventHandler,
    imageSize,
    setImageSrc,
    cameraZoomLevel,
    setCameraZoomLevel,
    currentIcon,
    setCurrentIcon,
    hideTilesets,
    // setHideTilesets,
    currentDisplayName,
    setCurrentDisplayName,
    centerPoint,
    primitiveCollection,
    currentPanelRotation,
    setCurrentPanelRotation,
  } = usePlacementPageContext();

  const { setIsFullWidth } = useFullWidthContext();

  const selectedSOPicture =
    (() => {
      // console.log('selected so pic', selectedSO);
      if (!selectedSO) return null;
      if (selectedSO.__typename === 'Picture') {
        return selectedSO.thumbnailUrl;
      }
      return selectedSO.pictures?.at(0)?.thumbnailUrl;
    })() ?? undefined;

  const [globeVisibility, setGlobeVisibility] = useState(true);
  const [outcropGeorefVisibility, setOutcropGeorefVisibility] = useState(false);

  const [infoBoxHandler, setInfoBoxHandler] =
    useState<ScreenSpaceEventHandler>();

  function setOutcropCenterOutlineVisibility(visible: boolean) {
    if (centerPoint) {
      centerPoint.show = visible;
    }
    if (primitiveCollection) {
      primitiveCollection.show = visible;
    }
    setOutcropGeorefVisibility(!outcropGeorefVisibility);
  }
  useEffect(() => {
    return () => {
      setIsFullWidth(false);
    };
  }, [setIsFullWidth]);

  useEffect(() => {
    setImageSrc(selectedSOPicture);
  }, [selectedSOPicture, setImageSrc]);

  useEffect(() => {
    if (infoBoxHandler) {
      infoBoxHandler.destroy();
    }
    if (viewer) {
      const handler = enableEntityInfoBox(viewer, entity => {
        console.log('entity', entity);
      });
      setInfoBoxHandler(handler);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewer]);

  const [currentImageSize, setCurrentImageSize] = useState<{
    width: number;
    height: number;
  }>(imageSize);

  const [createSOPlacement, { loading: loadingCreate }] = useMutation(
    createSOPlacementMutation,
  );

  const [updateSOPlacement] = useMutation(updateSOPlacementMutation);
  const [updateSOPlacementLocation] = useMutation(
    updateSOPlacementLocationMutation,
  );
  const [updateSOPlacementDefaultCamera] = useMutation(
    updateSOPlacementDefaultCameraMutation,
  );

  async function createOrUpdateSO() {
    const camera = getCameraCartographic(viewer);
    if (!camera || !currentPlacement || !selectedSO || !selectedSO.__typename) {
      console.log('return early', camera, currentPlacement, selectedSO);
      showPlacementMode(false, null);
      return;
    }
    const refetchQueries: [
      PureQueryOptions<OutcropSoPlacementsPageQueryVariables>,
    ] = [
      {
        query: outcropSOPlacementsPageQuery,
        variables: { id: outcrop.id },
      },
    ];

    if (selectedSO.placement) {
      const vars: UpdateSupportingObjectPlacementMutationVariables = {
        id: selectedSO.placement.id,
        input: {
          panelHeight: currentImageSize.height,
          panelWidth: currentImageSize.width,
          type: currentEntityType,
          icon: currentIcon,
          displayName: selectedSO.placement.displayName,
        },
      };
      const locationVars: UpdateSupportingObjectPlacementLocationMutationVariables =
        {
          id: selectedSO.placement.id,
          input: {
            location: {
              latitude: currentPlacement.latitude,
              longitude: currentPlacement.longitude,
              height: Math.floor(currentPlacement.height),
              heading: currentPlacement.heading,
              pitch: currentPlacement.pitch,
              roll: currentPlacement.roll,
            },
          },
        };
      const defaultCameraVars: UpdateSupportingObjectDefaultCameraMutationVariables =
        {
          id: selectedSO.placement.id,
          input: {
            location: {
              latitude: camera.latitude,
              longitude: camera.longitude,
              height: camera.height,
              heading: camera.heading,
              pitch: camera.pitch,
              roll: camera.roll,
            },
          },
        };
      updateSOPlacement({ variables: vars, refetchQueries });
      updateSOPlacementLocation({ variables: locationVars, refetchQueries });
      updateSOPlacementDefaultCamera({
        variables: defaultCameraVars,
        refetchQueries,
      });
    } else {
      type ParentType = NonNullable<typeof selectedSO>['__typename'];
      const pickIdIfType = (so: SO) => (property: ParentType) => {
        if (so.__typename === property) {
          return parseInt(so.id);
        }
        return null;
      };
      const pickId = pickIdIfType(selectedSO);

      const vars: CreateSupportingObjectPlacementMutationVariables = {
        input: {
          type: currentEntityType,
          icon: currentIcon,
          displayName: currentDisplayName || null,
          panelHeight: currentImageSize.height,
          panelWidth: currentImageSize.width,
          crossSectionId: pickId('CrossSection'),
          faciesSchemaId: pickId('FaciesSchema'),
          fieldPictureId: pickId('FieldPicture'),
          gigaPanId: pickId('GigaPan'),
          miniModelId: pickId('MiniModel'),
          photo360Id: pickId('Photo360'),
          pictureId: pickId('Picture'),
          productionId: pickId('Production'),
          reservoirModelId: pickId('ReservoirModel'),
          sedimentaryLogId: pickId('SedimentaryLog'),
          trainingImageId: pickId('TrainingImage'),
          variogramId: pickId('Variogram'),
          videoId: pickId('Video'),
          wellLogId: pickId('WellLog'),
        },
      };
      const res = await createSOPlacement({ variables: vars, refetchQueries });
      invariant(
        res.data?.supportingObjectPlacementCreate?.result?.id,
        'No id for new placement',
      );
      const placementVars: UpdateSupportingObjectPlacementLocationMutationVariables =
        {
          id: res.data?.supportingObjectPlacementCreate?.result?.id,
          input: {
            location: {
              latitude: currentPlacement.latitude,
              longitude: currentPlacement.longitude,
              height: Math.floor(currentPlacement.height),
              heading: currentPlacement.heading,
              pitch: currentPlacement.pitch,
              roll: currentPlacement.roll,
            },
          },
        };
      const cameraVars: UpdateSupportingObjectDefaultCameraMutationVariables = {
        id: res.data?.supportingObjectPlacementCreate?.result?.id,
        input: {
          location: {
            latitude: camera.latitude,
            longitude: camera.longitude,
            height: camera.height,
            heading: camera.heading,
            pitch: camera.pitch,
            roll: camera.roll,
          },
        },
      };
      const locationRes = await updateSOPlacementLocation({
        variables: placementVars,
        refetchQueries,
      });
      const cameraRes = await updateSOPlacementDefaultCamera({
        variables: cameraVars,
        refetchQueries,
      });
      console.log('Result: ', res, locationRes, cameraRes);
      console.log('saving');
    }
    showPlacementMode(false, null);
  }

  async function updateLabel(entity: Entity | null, displayName: string) {
    if (!entity || !viewer) {
      return;
    }
    updateEntityLabel(entity, displayName);
  }

  async function changeEntityType(
    type: CesiumPlacementType,
    item: SO,
    entity: Entity | null,
    initialWidth?: number,
    initialHeight?: number,
  ) {
    if (!viewer || !entity || !entity.position) {
      console.log('returned early', viewer, entity);
      return;
    }
    const currentEntityPlacement = entity.position.getValue(
      JulianDate.fromDate(new Date()),
    );
    const llh = convertFromCartesian(currentEntityPlacement);
    const entityId = entity.id;
    // @ts-ignore (only way to get entity children)
    entity._children.forEach(e => {
      viewer.entities.remove(e);
    });
    viewer.entities.remove(entity);
    const llhhpr: LatLngHeightHPR = { ...llh, heading: 0, pitch: 0, roll: 0 };
    if (type === CesiumPlacementType.Panel) {
      let picture = item.pictures?.at(0)?.thumbnailUrl;
      if (!picture && item.file) {
        picture = item.thumbnailUrl;
      }
      if (!picture) {
        console.log('something');
        return;
      }
      const width =
        initialWidth ||
        currentImageSize.width ||
        item.placement?.panelWidth ||
        imageSize.width;
      const height =
        initialHeight ||
        currentImageSize.height ||
        item.placement?.panelHeight ||
        imageSize.height;
      setCurrentImageSize({ width, height });
      console.log('width', item.placement, currentImageSize, imageSize);
      const e = addWallImage(
        viewer,
        entityId,
        picture,
        llh,
        item.placement?.displayName || currentDisplayName || item.name,
        width,
        height,
      );
      setCurrentEntityType(CesiumPlacementType.Panel);
      setCurrentEntity(e);
      console.log(e);
    } else if (type === CesiumPlacementType.Point) {
      let picture = item.pictures?.at(0)?.thumbnailUrl;
      if (!picture && item.file) {
        picture = item.thumbnailUrl;
      }
      if (picture) {
        const e = await addInfoBoxIconPoint({
          viewer,
          id: entityId,
          panelUrl: picture,
          position: llhhpr,
          label: item.placement?.displayName || currentDisplayName || item.name,
          iframe: false,
          iconName: currentIcon || item.placement?.icon,
          width: item.placement?.panelWidth ?? undefined,
          height: item.placement?.panelHeight ?? undefined,
        });
        setCurrentEntityType(CesiumPlacementType.Point);
        // todo: use panel when its a real panel
        // setCurrentEntityType(SupportingObjectPlacementType.Panel);
        setCurrentEntity(e);

        if (eventHandler) {
          eventHandler.destroy();
        }
        const eh = await enableEntityClickPlacement(viewer, e, p => {
          setCurrentPlacement(p);
        });
        setEventHandler(eh);
      } else if (item.url) {
        const e = await addInfoBoxIconPoint({
          viewer,
          id: entityId,
          panelUrl: item.url,
          position: llhhpr,
          label: item.placement?.displayName || currentDisplayName || item.name,
          iframe: true,
          iconName: currentIcon || item.placement?.icon,
          width: item.placement?.panelWidth ?? undefined,
          height: item.placement?.panelHeight ?? undefined,
        });
        setCurrentEntityType(CesiumPlacementType.Point);
        // todo: use panel when its a real panel
        // setCurrentEntityType(SupportingObjectPlacementType.Panel);
        setCurrentEntity(e);

        if (eventHandler) {
          eventHandler.destroy();
        }
        const eh = await enableEntityClickPlacement(viewer, e, p => {
          setCurrentPlacement(p);
        });
        setEventHandler(eh);
      } else {
        setCurrentEntityType(CesiumPlacementType.Point);
        const e = await addIconPoint({
          viewer,
          id: entityId,
          icon: currentIcon || item.placement?.icon,
          position: llhhpr,
          label: item.placement?.displayName || currentDisplayName || item.name,
        });
        setCurrentEntity(e);
        if (eventHandler) {
          eventHandler.destroy();
        }
        if (currentEntity) {
          const eh = await enableEntityClickPlacement(viewer, e, p => {
            setCurrentPlacement(p);
          });
          setEventHandler(eh);
        }
      }
    }
  }

  function handleMove(event: IJoystickUpdateEvent | null) {
    if (!event || !viewer) {
      return;
    }
    const now = JulianDate.now();
    if (event.type === 'move' && event.x && event.y) {
      const changedY = event.y * cameraZoomLevel; //joystickSpeed
      const changedX = event.x * cameraZoomLevel;
      const currentPosition = currentEntity?.position?.getValue(now);
      if (currentPosition && currentEntity) {
        const converted = convertFromCartesian(currentPosition);
        const newPositionllhhpr = {
          latitude: converted.latitude + changedX,
          longitude: converted.longitude + changedY,
          height: converted.height,
          heading: 0,
          pitch: 0,
          roll: 0,
        } satisfies LatLngHeightHPR;
        currentEntity.position = new ConstantPositionProperty(
          Cartesian3.fromDegrees(
            newPositionllhhpr.longitude,
            newPositionllhhpr.latitude,
            newPositionllhhpr.height,
          ),
        );
        //@ts-ignore (_children is private but theres no other way to access entity children)
        currentEntity._children.forEach(e => {
          e.position = new ConstantPositionProperty(
            Cartesian3.fromDegrees(
              newPositionllhhpr.longitude,
              newPositionllhhpr.latitude,
              newPositionllhhpr.height,
            ),
          );
          if (e.wall) {
            const positions = e.wall.positions.getValue(now);
            const firstPositionCarto = convertFromCartesian(positions[0]);
            const lastPositionCarto = convertFromCartesian(positions[1]);
            const newFirstPosition = Cartesian3.fromDegrees(
              firstPositionCarto.longitude + changedY,
              firstPositionCarto.latitude + changedX,
              firstPositionCarto.height,
            );
            const newLastPosition = Cartesian3.fromDegrees(
              lastPositionCarto.longitude + changedY,
              lastPositionCarto.latitude + changedX,
              lastPositionCarto.height,
            );
            e.wall.positions = new ConstantProperty([
              newFirstPosition,
              newLastPosition,
            ]);
          }
        });
        setCurrentPlacement(newPositionllhhpr);
      }
    }
  }

  function handleMoveHeight(event: IJoystickUpdateEvent | null) {
    if (!event || !viewer) {
      return;
    }
    const now = JulianDate.now();
    if (event.type === 'move' && event.y) {
      const changedY = event.y * cameraZoomLevel * 10000; //joystickSpeed
      const currentPosition = currentEntity?.position?.getValue(now);
      if (currentPosition && currentEntity) {
        const converted = convertFromCartesian(currentPosition);
        const newPositionllhhpr = {
          latitude: converted.latitude,
          longitude: converted.longitude,
          height: converted.height + changedY,
          heading: 0,
          pitch: 0,
          roll: 0,
        } satisfies LatLngHeightHPR;
        currentEntity.position = new ConstantPositionProperty(
          Cartesian3.fromDegrees(
            newPositionllhhpr.longitude,
            newPositionllhhpr.latitude,
            newPositionllhhpr.height,
          ),
        );
        //@ts-ignore (_children is private but theres no other way to access entity children)
        currentEntity._children.forEach(e => {
          e.position = new ConstantPositionProperty(
            Cartesian3.fromDegrees(
              newPositionllhhpr.longitude,
              newPositionllhhpr.latitude,
              newPositionllhhpr.height,
            ),
          );
          if (e.wall) {
            e.wall.minimumHeights = new ConstantProperty([
              newPositionllhhpr.height,
              newPositionllhhpr.height,
            ]);
            const positions = e.wall.positions.getValue(now);
            const firstPosition = positions[0];
            const lastPosition = positions[1];
            const firstPositionCarto =
              Cartographic.fromCartesian(firstPosition);
            const lastPositionCarto = Cartographic.fromCartesian(lastPosition);
            const newFirstPosition = Cartesian3.fromRadians(
              firstPositionCarto.longitude,
              firstPositionCarto.latitude,
              firstPositionCarto.height + changedY,
            );
            const newLastPosition = Cartesian3.fromRadians(
              lastPositionCarto.longitude,
              lastPositionCarto.latitude,
              lastPositionCarto.height + changedY,
            );
            e.wall.positions = new ConstantProperty([
              newFirstPosition,
              newLastPosition,
            ]);
          }
        });
        setCurrentPlacement(newPositionllhhpr);
      }
    }
  }

  function cameraCallback(viewer: Viewer) {
    const dist = getCurrentZoomDistance(viewer);
    if (!dist) {
      setCameraZoomLevel(0.00001);
    } else {
      if (dist < 250) {
        setCameraZoomLevel(0.00001);
      } else if (dist < 500) {
        setCameraZoomLevel(0.000025);
      } else if (dist < 1000) {
        setCameraZoomLevel(0.00005);
      } else if (dist < 2000) {
        setCameraZoomLevel(0.0001);
      }
    }
  }

  async function changeIcon(icon: string, item: SO, entity: Entity | null) {
    setCurrentIcon(icon);
    if (!viewer || !entity || !entity.position) {
      console.log('returned early', viewer, entity);
      return;
    }
    const currentEntityPlacement = entity.position.getValue(
      JulianDate.fromDate(new Date()),
    );
    const llh = convertFromCartesian(currentEntityPlacement);
    const entityId = entity.id;
    // @ts-ignore (only way to get entity children)
    entity._children.forEach(e => {
      viewer.entities.remove(e);
    });
    viewer.entities.remove(entity);
    const llhhpr: LatLngHeightHPR = { ...llh, heading: 0, pitch: 0, roll: 0 };
    let picture = item.pictures?.at(0)?.thumbnailUrl;
    if (!picture && item.thumbnailUrl) {
      picture = item.thumbnailUrl;
    }
    if (picture) {
      const e = await addInfoBoxIconPoint({
        viewer,
        id: entityId,
        panelUrl: picture,
        position: llhhpr,
        label: item.placement?.displayName || currentDisplayName || item.name,
        iframe: false,
        iconName: icon,
        width: item.placement?.panelWidth ?? undefined,
        height: item.placement?.panelHeight ?? undefined,
      });
      setCurrentEntityType(CesiumPlacementType.Point);
      // todo: use panel when its a real panel
      // setCurrentEntityType(SupportingObjectPlacementType.Panel);
      setCurrentEntity(e);

      if (eventHandler) {
        eventHandler.destroy();
      }
      const eh = await enableEntityClickPlacement(viewer, e, p => {
        setCurrentPlacement(p);
      });
      setEventHandler(eh);
    } else if (item.url) {
      const e = await addInfoBoxIconPoint({
        viewer,
        id: entityId,
        panelUrl: item.url,
        position: llhhpr,
        label: item.placement?.displayName || currentDisplayName || item.name,
        iframe: true,
        iconName: icon,
        width: item.placement?.panelWidth ?? undefined,
        height: item.placement?.panelHeight ?? undefined,
      });
      setCurrentEntityType(CesiumPlacementType.Point);
      // todo: use panel when its a real panel
      // setCurrentEntityType(SupportingObjectPlacementType.Panel);
      setCurrentEntity(e);

      if (eventHandler) {
        eventHandler.destroy();
      }
      const eh = await enableEntityClickPlacement(viewer, e, p => {
        setCurrentPlacement(p);
      });
      setEventHandler(eh);
    } else {
      const e = await addIconPoint({
        viewer,
        id: entityId,
        icon,
        position: llhhpr,
        label: item.placement?.displayName || currentDisplayName || item.name,
      });
      setCurrentEntity(e);
      if (eventHandler) {
        eventHandler.destroy();
      }
      const eh = await enableEntityClickPlacement(viewer, e, p => {
        setCurrentPlacement(p);
      });
      setEventHandler(eh);
    }
  }

  function updateWallPositions(angle: number) {
    if (currentEntity && currentEntity.position) {
      const currentPositions = getCurrentWallPosition(currentEntity);
      if (currentPositions) {
        const positions = currentPositions.getValue(new JulianDate(0));
        const rotationPoint = currentEntity.position.getValue(
          new JulianDate(0),
        );
        if (rotationPoint) {
          const firstPosition = rotateCartesian3(
            positions[0],
            rotationPoint,
            angle,
          );
          const lastPosition = rotateCartesian3(
            positions[1],
            rotationPoint,
            angle,
          );
          console.log(positions, firstPosition, lastPosition);
          updateCurrentWallPosition(currentEntity, [
            firstPosition,
            lastPosition,
          ]);
        }
      }
    }
  }

  return (
    <div className="grid lg:grid-cols-4 gap-6">
      <div className="max-h-screen overflow-auto">
        <Sidebar outcrop={outcrop} />
      </div>
      <div className="lg:col-span-3">
        <CesiumViewer
          sendCesiumViewer={setViewer}
          hideTilesets={hideTilesets}
          initialTilesets={visibleTilesets}
          showGlobe={globeVisibility}
          terrainProvider={TerrainProvider.World}
          cameraCallback={cameraCallback}
          infoBox={true}
        />
        <Button
          onClick={() => {
            setIsFullWidth(prev => !prev);
          }}
          color="primary"
          size="md"
          className="gap-1 my-1"
        >
          Toggle Immersive Mode
        </Button>
        <Button
          onClick={() => {
            setGlobeVisibility(prev => !prev);
          }}
          color="primary"
          size="md"
          className="gap-1 my-1 mx-1"
        >
          Toggle Globe
        </Button>
        {(centerPoint || primitiveCollection) && (
          <Button
            onClick={() => {
              setOutcropCenterOutlineVisibility(!outcropGeorefVisibility);
            }}
            color="primary"
            size="md"
            className="gap-1 my-1 mx-1"
          >
            Toggle Outcrop GeoRef
          </Button>
        )}
        {placementMode && selectedSO && (
          <>
            <Heading level={3}>Place {selectedSO.name}</Heading>
            <div className="grid lg:grid-cols-2 gap-6">
              {selectedSOPicture && (
                <div>
                  <img
                    src={selectedSOPicture}
                    className="max-w-full h-auto"
                    alt={selectedSO.file?.name ?? 'Picture'}
                  />
                </div>
              )}

              <div>
                <p>Alt+Click to place Supporting Object</p>
                <p>Saving will save both placement and camera view</p>
                <label className="label">
                  <input
                    type="radio"
                    className="radio"
                    name="type"
                    defaultChecked
                    onChange={() => {
                      changeEntityType(
                        CesiumPlacementType.Point,
                        selectedSO,
                        currentEntity,
                      );
                    }}
                  />
                  Point
                </label>
                <label className="label">
                  <input
                    type="radio"
                    className="radio"
                    name="type"
                    onChange={() => {
                      changeEntityType(
                        CesiumPlacementType.Panel,
                        selectedSO,
                        currentEntity,
                      );
                    }}
                  />
                </label>
                <div className="flex flex-col gap-2">
                  <div>
                    <label className="pr-1" htmlFor="displayName">
                      Display Name
                    </label>
                    <input
                      type="text"
                      name="displayName"
                      placeholder={`${selectedSO.placement?.displayName || selectedSO.name}`}
                      onChange={v => {
                        const newSO = { ...selectedSO };
                        if (newSO.placement) {
                          newSO.placement = {
                            ...newSO.placement,
                            ...{ displayName: v.target.value },
                          };
                          setSelectedSO(newSO);
                        }
                        setCurrentDisplayName(v.target.value);
                        updateLabel(currentEntity, v.target.value);
                      }}
                    />
                  </div>
                  <IconSelector
                    value="icon"
                    onChange={icon => {
                      changeIcon(icon, selectedSO, currentEntity);
                    }}
                  />
                </div>
                <div className="items-center text-center my-2 mx-auto flex">
                  <SignalJoystick
                    size={100}
                    label={'Lat/Lng'}
                    signalRate={50}
                    callback={handleMove}
                    throttle={50}
                  />
                  <SignalJoystick
                    label={'Height'}
                    size={100}
                    signalRate={50}
                    callback={handleMoveHeight}
                    throttle={50}
                    controlPlaneShape={JoystickShape.AxisY}
                    baseShape={JoystickShape.Square}
                    stickShape={JoystickShape.Square}
                  />
                </div>

                {/* todo: update this after thumbnail */}
                {currentEntityType === CesiumPlacementType.Panel && (
                  <div className="text-center form-control">
                    <label className="label">
                      <span className="label-text">Panel Height (m)</span>
                    </label>
                    <input
                      name="height"
                      type="number"
                      id="panel-height"
                      placeholder="height"
                      className="input"
                      value={currentImageSize.height || imageSize.height}
                      onChange={v => {
                        const newHeight = v.target.value
                          ? parseInt(v.target.value)
                          : 0;
                        const newWidth =
                          newHeight === 0
                            ? 0
                            : (imageSize.width / imageSize.height) * newHeight;
                        setCurrentImageSize({
                          width: newWidth,
                          height: newHeight,
                        });
                        console.log('new', newWidth, newHeight);
                        // setWallSize(viewer, currentEntity, newWidth, newHeight);
                        console.log('chaing height');
                        changeEntityType(
                          CesiumPlacementType.Panel,
                          selectedSO,
                          currentEntity,
                          newWidth,
                          newHeight,
                        );
                      }}
                    />
                    <label className="label">
                      <span className="label-text">Panel Width (m)</span>
                    </label>
                    <input
                      name="width"
                      type="number"
                      id="panel-width"
                      placeholder="width"
                      className="input"
                      value={currentImageSize.width || imageSize.width}
                      onChange={v => {
                        const newWidth = v.target.value
                          ? parseInt(v.target.value)
                          : 0;
                        const newHeight =
                          newWidth === 0
                            ? 0
                            : (imageSize.height / imageSize.width) * newWidth;
                        setCurrentImageSize({
                          width: newWidth,
                          height: newHeight,
                        });
                        changeEntityType(
                          CesiumPlacementType.Panel,
                          selectedSO,
                          currentEntity,
                          newWidth,
                          newHeight,
                        );
                      }}
                    />
                    <label className="label">
                      <span className="label-text">Panel Rotation</span>
                    </label>
                    <Button
                      type="button"
                      onClick={v => {
                        updateWallPositions(-1);
                      }}
                    >
                      {'<'}
                    </Button>
                    <Button
                      type="button"
                      onClick={v => {
                        updateWallPositions(1);
                      }}
                    >
                      {'>'}
                    </Button>
                  </div>
                )}

                <div className="space-x-1">
                  <Button
                    type="submit"
                    onClick={createOrUpdateSO}
                    disabled={loadingCreate}
                    color="primary"
                  >
                    Save
                  </Button>
                  <Button
                    type="button"
                    color="ghost"
                    onClick={() => {
                      showPlacementMode(false, null);
                      if (currentEntity && viewer) {
                        //@ts-ignore (_children is private but theres no other way to access entity children)
                        currentEntity._children.forEach(e => {
                          viewer.entities.remove(e);
                        });
                        viewer.entities.remove(currentEntity);
                      }
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const filterByOutcropTag = (outcropId: number) => (so: SO) =>
  so.outcropTagId === outcropId;

const studyHasPlaceableContent = (outcropId: number) => (study: Study) => {
  const soFilter = filterByOutcropTag(outcropId);
  const hasPicture = (so: SO | Study) => (so.pictures?.length ?? 0) > 0;

  const picture = hasPicture(study);
  if (picture) return true;
  const cs = study.crossSections.filter(soFilter).find(hasPicture);
  if (cs) return true;
  const facies = study.facies.filter(soFilter).find(hasPicture);
  if (facies) return true;
  const sl = study.sedimentaryLogs.filter(soFilter).find(hasPicture);
  if (sl) return true;
  const wl = study.wellLogs.filter(soFilter).find(hasPicture);
  if (wl) return true;
  const p = study.production.filter(soFilter).find(hasPicture);
  if (p) return true;
  const rm = study.reservoirModels.filter(soFilter).find(hasPicture);
  if (rm) return true;
  const ti = study.trainingImages.filter(soFilter).find(hasPicture);
  if (ti) return true;
  const v = study.variograms.filter(soFilter).find(hasPicture);
  if (v) return true;
  const gp = study.gigaPans.filter(soFilter).find(hasPicture);
  if (gp) return true;

  return false;
};

function Sidebar({ outcrop }: { outcrop: Outcrop }) {
  const outcropId = parseInt(outcrop.id);
  const studies = R.pipe(
    () => outcrop.studies,
    R.uniqBy(study => study.id),
    R.sortBy(study => study.name ?? ''),
    R.filter(studyHasPlaceableContent(outcropId)),
  )();

  const voms = outcrop.virtualOutcropModels;

  return (
    <div className="space-y-4">
      {!!voms.length && (
        <ExpandableSection heading="Virtual Outcrops">
          <div className="space-y-1">
            {voms.map(vom => (
              <VomItem key={`vom-${vom.id}`} vom={vom} />
            ))}
          </div>
        </ExpandableSection>
      )}

      <OutcropSection outcrop={outcrop} />

      {!!studies.length && (
        <ExpandableSection heading="Studies">
          <div className="space-y-2">
            {studies.map(study => (
              <StudySection
                key={study.id}
                outcropId={outcropId}
                study={study}
              />
            ))}
          </div>
        </ExpandableSection>
      )}
    </div>
  );
}

function ExpandableSection({
  heading,
  children,
}: {
  heading: ReactNode;
  children: ReactNode;
}) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex justify-between items-center pr-2"
      >
        <div className="grow shrink-0 text-left max-w-full px-2">
          {typeof heading === 'string' ? (
            <Heading level={3}>{heading}</Heading>
          ) : (
            heading
          )}
        </div>
        <div className="shrink-0 ">
          <ExpandedIcon expanded={expanded} />
        </div>
      </button>

      {expanded && children}
    </div>
  );
}

function OutcropSection({ outcrop }: { outcrop: Outcrop }) {
  const outcropId = parseInt(outcrop.id);
  return (
    <div className="border border-slate-100 p-2">
      <Heading level={4} className="sticky top-0 mt-0 bg-white">
        {outcrop.name}
        <ItemLink to={uploadOutcropUpdateRoute(outcropId)} />
      </Heading>
      <div className="space-y-2">
        <SOSection label="Pictures" items={outcrop.pictures} />
        <SOSection label="Cross Sections" items={outcrop.crossSections} />
        <SOSection label="Facies" items={outcrop.facies} />
        <SOSection label="Sedimentary Logs" items={outcrop.sedimentaryLogs} />
        <SOSection label="Well Logs" items={outcrop.wellLogs} />
        <SOSection label="Production" items={outcrop.production} />
        <SOSection label="Reservoir Models" items={outcrop.reservoirModels} />
        <SOSection label="Training Images" items={outcrop.trainingImages} />
        <SOSection label="Variograms" items={outcrop.variograms} />
        <SOSection label="Giga Pans" items={outcrop.gigaPans} />
        <SOSection
          label="Field Pictures"
          items={outcrop.fieldPictures.map(fp => ({
            ...fp,
            id: fp.id,
            name: fp.file.name ?? `Field Picture ${fp.id}`,
          }))}
        />
        <SOSection label="Mini-Models" items={outcrop.miniModels} />
        <SOSection label="360 Photos" items={outcrop.photo360s} />
        <SOSection label="Videos" items={outcrop.videos} />
      </div>
    </div>
  );
}

function StudySection({
  outcropId,
  study,
}: {
  outcropId: number;
  study: Study;
}) {
  const studyId = parseInt(study.id);
  const soFilter = filterByOutcropTag(outcropId);

  return (
    <div className="border border-slate-100 p-2">
      <ExpandableSection
        heading={
          <div className="w-full">
            <Heading
              level={4}
              className="text-black sticky top-0 bg-white truncate max-w-full"
            >
              {study.name}
            </Heading>
            <div className="text-sm">
              {[study.collectedYear, study.collectedBy]
                .filter(rejectNil)
                .join(' - ')}
              <ItemLink to={studyRoute(studyId)} />
            </div>
          </div>
        }
      >
        <div className="space-y-2">
          <SOSection label="Pictures" items={study.pictures.filter(soFilter)} />
          <SOSection
            label="Cross Sections"
            items={study.crossSections.filter(soFilter)}
          />
          <SOSection label="Facies" items={study.facies.filter(soFilter)} />
          <SOSection
            label="Sedimentary Logs"
            items={study.sedimentaryLogs.filter(soFilter)}
          />
          <SOSection
            label="Well Logs"
            items={study.wellLogs.filter(soFilter)}
          />
          <SOSection
            label="Production"
            items={study.production.filter(soFilter)}
          />
          <SOSection
            label="Reservoir Models"
            items={study.reservoirModels.filter(soFilter)}
          />
          <SOSection
            label="Training Images"
            items={study.trainingImages.filter(soFilter)}
          />
          <SOSection
            label="Variograms"
            items={study.variograms.filter(soFilter)}
          />
          <SOSection
            label="Giga Pans"
            items={study.gigaPans.filter(soFilter)}
          />
        </div>
      </ExpandableSection>
    </div>
  );
}

function SOSection({ label, items }: { label: string; items: SO[] }) {
  if (!items.length) return null;

  const sortedItems = R.sortBy(item => item.name.toLowerCase(), items);

  return (
    <div>
      <div className="text-xs text-muted">{label}</div>
      <div className="space-y-1">
        {sortedItems.map(item => (
          <SOItem key={item.id} item={item} />
        ))}
      </div>
    </div>
  );
}

function SOItem({ item }: { item: SO }) {
  const {
    // toggleSOVisibility,
    showPlacementMode,
    viewer,
    eventHandler,
    setEventHandler,
    setCurrentPlacement,
    setCurrentEntity,
    // flyToLocation,
  } = usePlacementPageContext();
  const showMenuInitial =
    item.placement?.lifecycleStatus !== LifecycleStatus.Approved;
  const [isHovered, setIsHovered] = useState(false);
  const [showMenu, setShowMenu] = useState(showMenuInitial);
  const hasPlacement = !!item.placement;

  async function triggerMenu() {
    setShowMenu(!showMenu);
  }

  async function showUnplacedItem(viewer: Viewer | null, item: SO) {
    if (!viewer) {
      return;
    }
    if (eventHandler) {
      eventHandler.destroy();
    }
    const defaultIcon =
      item && item.__typename ? iconDefaults[item.__typename] : 'Marker';
    const e = await addIconPoint({
      viewer,
      id: `${item.__typename}-${item.id}`,
      icon: defaultIcon,
      position: {
        latitude: 0,
        longitude: 0,
        height: 0,
        heading: 0,
        pitch: 0,
        roll: 0,
      },
      label: item.placement?.displayName || item.name,
    });
    setCurrentEntity(e);
    const eh = await enableEntityClickPlacement(viewer, e, p => {
      setCurrentPlacement(p);
    });
    setEventHandler(eh);
  }

  return (
    <div>
      <div
        className="flex justify-between gap-2 w-full items-start"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <label
          className={cn(
            'label items-start border-l-2 border-slate-200 border-dotted grow pt-1',
            {
              truncate: !isHovered,
              'text-slate-800 pl-2.5': hasPlacement,
              'text-slate-400 pl-8': !hasPlacement,
            },
          )}
        >
          <div
            className={cn('w-full', {
              truncate: !isHovered,
            })}
          >
            {item.placement?.displayName ?? item.name}
          </div>
        </label>
        {item.placement && (
          <Button type="button" color="ghost" size="xs" onClick={triggerMenu}>
            <ExpandedIcon expanded={showMenu} />
          </Button>
        )}
        {!hasPlacement && (
          <Button
            type="button"
            color="primary"
            size="xs"
            onClick={() => {
              showPlacementMode(true, item);
              showUnplacedItem(viewer, item);
            }}
          >
            <FontAwesomeIcon icon={faPlus} />
          </Button>
        )}
      </div>
      {item.placement && <SOItemMenu show={showMenu} item={item} />}
    </div>
  );
}

function SOItemMenu({ item, show }: { item: SO; show: boolean }) {
  const {
    toggleSOVisibility,
    showPlacementMode,
    viewer,
    eventHandler,
    setEventHandler,
    setCurrentPlacement,
    setCurrentEntity,
    flyToLocation,
  } = usePlacementPageContext();

  const [isVisible, setIsVisible] = useState(true);
  const [isLabelVisible, setIsLabelVisible] = useState(true);

  async function editPlacement(item: SO) {
    const entityId = `${item.__typename}-${item.id}`;
    const e = viewer?.entities.getById(entityId);
    if (viewer && e) {
      if (eventHandler) {
        eventHandler.destroy();
      }
      setCurrentEntity(e);
      const currentEntityPosition = e.position?.getValue(
        new JulianDate(Date.now()),
      );
      if (currentEntityPosition) {
        setCurrentPlacement(convertCartesian3Tollhhpr(currentEntityPosition));
      }
      const eh = await enableEntityClickPlacement(viewer, e, p => {
        setCurrentPlacement(p);
      });
      setEventHandler(eh);
    }
  }

  function handleToggleVisibility() {
    const nextVisible = toggleSOVisibility(`${item.__typename}-${item.id}`);
    setIsVisible(nextVisible);
  }

  function handleFlyTo() {
    if (!item.placement?.defaultCamera) return;
    flyToLocation(item.placement.defaultCamera);
  }

  function handleLabelDisplay() {
    toggleEntityLabelVisibility(viewer, `${item.__typename}-${item.id}`);
    setIsLabelVisible(!isLabelVisible);
  }

  return (
    <div className={cn({ hidden: !show })}>
      <ul className="menu menu-horizontal bg-base-200 rounded-box items-center p-0 ml-1 justify-between px-2 w-full">
        <li>
          <Button type="button" color="ghost" size="xs" onClick={handleFlyTo}>
            <FontAwesomeIcon icon={faVideoCamera} />
          </Button>
        </li>
        <li>
          <Button
            type="button"
            color="ghost"
            size="xs"
            onClick={handleLabelDisplay}
          >
            {isLabelVisible && <FontAwesomeIcon icon={faFont} />}
            {!isLabelVisible && <FontAwesomeIcon icon={faTextSlash} />}
          </Button>
        </li>
        <li>
          <Button
            type="button"
            color="ghost"
            size="xs"
            onClick={handleToggleVisibility}
          >
            {isVisible && <FontAwesomeIcon icon={faEye} />}
            {!isVisible && <FontAwesomeIcon icon={faEyeSlash} />}
          </Button>
        </li>
        <li>
          <Button
            type="button"
            color="ghost"
            size="xs"
            onClick={() => {
              showPlacementMode(true, item);
              editPlacement(item);
            }}
          >
            <FontAwesomeIcon icon={faPenToSquare} />
          </Button>
        </li>
        {item.placement && (
          <li>
            <SOPlacementStatusModal placement={item.placement} />
          </li>
        )}
      </ul>
    </div>
  );
}

function VomItem({ vom }: { vom: Vom }) {
  const { flyToLocation, visibleTilesets, hideTilesets, setHideTilesets } =
    usePlacementPageContext();
  const [isVisible, setIsVisible] = useState(true);

  const tsAsset = vom.cesiumAsset;
  const token = tsAsset?.tilesetToken?.token;
  const camera = tsAsset?.defaultCamera;

  function handleFlyTo() {
    if (!camera) return;
    flyToLocation(camera);
  }

  function handleToggleVisibility() {
    const ts = visibleTilesets.filter(ts => {
      return ts.assetToken === token;
    });
    if (ts.length > 0) {
      if (hideTilesets.includes(ts[0])) {
        setHideTilesets(hideTilesets.filter(ts => ts.assetToken !== token));
      } else {
        setHideTilesets([...hideTilesets, ts[0]]);
      }
    }
    setIsVisible(!isVisible);
  }

  return (
    <div className="flex justify-between gap-6 w-full items-center">
      <div className="text-left flex">
        <input
          type="checkbox"
          className="checkbox checkbox-xs"
          checked={!!token && isVisible}
          onChange={handleToggleVisibility}
          disabled={!token}
        />
        <div
          className={cn(
            'self-center ml-2 pl-2 border-l border-slate-200 border-dotted text-base max-w-full leading-4',
            {
              'text-slate-800': !!token,
              'text-slate-400': !token,
            },
          )}
        >
          {vom.name}
        </div>
      </div>

      <div className="text-right">
        {token && camera && (
          <Button type="button" color="ghost" size="xs" onClick={handleFlyTo}>
            <FontAwesomeIcon icon={faVideoCamera} />
          </Button>
        )}
      </div>
    </div>
  );
}

function ItemLink({ to }: { to: LinkProps['to'] }) {
  return (
    <Link
      to={to}
      target="_blank"
      className="text-muted hover:text-primary ml-1 text-sm"
    >
      <FontAwesomeIcon icon={faExternalLink} />
    </Link>
  );
}

export default function V4Wrapper() {
  return (
    <ApolloProviderV4>
      <UploadOutcropSOPlacementsPage />
    </ApolloProviderV4>
  );
}
